import _ from 'lodash';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { GetPatentTemplate, type ApiGetPatentTemplateResponse } from '@/infrastructure/api-functions/core-search-api/get-patent-template-api';
import { getIsDesignPatent } from '../../utils/get-is-design-patent';
import type { PatentLinkExtend } from '../../types';
import type { GetPatentLinkDTO, GetPatentLinkInput } from '../get-patent-link';

// KC位于专利号最后，可能是一个字母，或一个字母加一个数字。
const KC_REG = /[a-z]\d?$/i;

const ESPACENET_SUFFIX = ['A0', 'D0', 'P0', 'U0', 'V0'];

const FAMILY_ID_SYMBOL_REG = '@fam';

const PN_SYMBOL_REG = /@pn/g;

const KEY_SYMBOL_REG = /@key/gi;

const PAD_START_VALUE = '0';

const AU_OFFICIAL_LINK_TEMPLATE = 'https://ipsearch.ipaustralia.gov.au/patents/@key';

// 链接文案
const OFFICIAL_NAME_MAP: Record<string, string> = {
    DE: 'DEPATISnet',
    EU: 'TMDN',
    NO: 'NIPO',
    CN: 'CNIPA',
    CA: 'CAIPO',
    AU: 'AusPat',
    WO: 'WIPO',
};

// 专利公告日在20100331之前，但由于解密公告日在这日期之后，link也是需要带KD。一共65件专利，我抽查检验过，请单独处理下
// 另外，由于解密专利是不定期公开，且根据保密等级，可能会公开20100331之前的专利。
// 所以，针对解密专利更新的SIPO link，我们会不定期提供列表
const LIMIT_PBD = '2010-03-31';
const EXCEPT_PNS = [
    'CN106342077B',
    'CN106961833B',
    'CN103650659B',
    'CN105122969B',
    'CN106341999B',
    'CN104335706B',
    'CN106342090B',
    'CN104335707B',
    'CN104335708B',
    'CN104335709B',
    'CN106342252B',
    'CN105659796B',
    'CN106134363B',
    'CN106341995B',
    'CN106342070B',
    'CN106342150B',
    'CN105659789B',
    'CN107182251B',
    'CN106134438B',
    'CN105659787B',
    'CN106342071B',
    'CN106342065B',
    'CN105659786B',
    'CN106342015B',
    'CN103748986B',
    'CN105659756B',
    'CN106961834B',
    'CN106342397B',
    'CN106342311B',
    'CN106342172B',
    'CN106342012B',
    'CN105659750B',
    'CN105659812B',
    'CN106342063B',
    'CN105659759B',
    'CN105659871B',
    'CN106342093B',
    'CN106134324B',
    'CN104335705B',
    'CN105659831B',
    'CN105659837B',
    'CN106342034B',
    'CN106342164B',
    'CN102812796B',
    'CN106342168B',
    'CN106342165B',
    'CN106342395B',
    'CN105940777B',
    'CN106342057B',
    'CN106342009B',
    'CN106342394B',
    'CN106342000B',
    'CN106342095B',
    'CN106342278B',
    'CN105659836B',
    'CN106342300B',
    'CN107211575B',
    'CN106341988B',
    'CN106134374B',
    'CN105659872B',
    'CN105659845B',
    'CN105659811B',
    'CN105659844B',
    'CN106342014B',
    'CN106342209B',
];

@injectable()
export class GetPatentLinkDTOImpl implements GetPatentLinkDTO {
    constructor(
        @inject(GetPatentTemplate)
        private _getPatentTemplateApi: GetPatentTemplate,
    ) {
    }

    private _getKC(docdb: string) {
        const matched = docdb.match(KC_REG);
        return matched ? matched[0] : '';
    }

    private _padStart(str: string, count: number) {
        // 如果字符串str长度不足count，则在左侧补0
        return _.padStart(str, count, PAD_START_VALUE);
    }

    private _getOfficialLinkDE(pn: string, template: ApiGetPatentTemplateResponse) {
        if (/^DE(\d{1,12})([A-Z]\d?)$/i.test(pn)) {
            const key = `${this._padStart(RegExp.$1, 12)}${RegExp.$2}`;
            return template.DE.replace(KEY_SYMBOL_REG, key);
        }
    }

    private _getOfficialLinkEU(pn: string, template: ApiGetPatentTemplateResponse) {
        if (/^(EU)(\d{9})(\d{4})/i.test(pn)) {
            const key = `${RegExp.$2}-${RegExp.$3}`;
            return template.EM.replace(KEY_SYMBOL_REG, key);
        }
    }

    private _getOfficialLinkNO(apn: string, template: ApiGetPatentTemplateResponse) {
        // APN=NO+4位年份+3-4位数字
        if (/^NO(\d{7,8})$/i.test(apn)) {
            return template.NO.replace(KEY_SYMBOL_REG, RegExp.$1);
        }
    }

    private _getOfficialLinkWO(pn: string, template: ApiGetPatentTemplateResponse) {
        // PN=WO+10位数字+KC
        if (/^WO(\d{10})([A-Z]\d?)$/i.test(pn)) {
            return template.WO.replace(KEY_SYMBOL_REG, RegExp.$1);
        }
    }

    private _getOfficialLinkCNPn(pn: string) {
        const kc = this._getKC(pn);
        // 如果以C结尾不做截取
        if (kc !== 'C') {
            const lastKCIndex = pn.lastIndexOf(kc);
            return pn.substring(0, lastKCIndex);
        }
        return pn;
    }

    private _getOfficialLinkCN(pn: string, pbd: string, template: ApiGetPatentTemplateResponse) {
        // PN＝CN＋（7-9）位数＋KD
        if (/^CN(\d{7,9})([A-Z]\d?)$/i.test(pn)) {
            const isBeforeLimitPbd = new Date(pbd).getTime() < new Date(LIMIT_PBD).getTime();
            const isExceptPn = EXCEPT_PNS.indexOf(pn) > -1;
            let _pn = pn;

            // 如专利的公开日在2010.04.07之后（包含当天），参数PN带有KD
            // 如专利的公开日在2010.03.31之前（包含当天），参数PN的KD去掉
            if (isBeforeLimitPbd && !isExceptPn) {
                _pn = this._getOfficialLinkCNPn(pn);
            }

            return template.CN.replace(KEY_SYMBOL_REG, _pn);
        }
    }

    private _getOfficialLinkCAPt(type: string[]) {
        let pt: string = '';

        for (let index = 0; index < type.length; index++) {
            if (type[index].length === 3) {
                pt = type[index];
                break;
            }
        }

        return pt;
    }

    private _getOfficialLinkCA(pn: string, apn: string, type: string[], template: ApiGetPatentTemplateResponse) {
        // PN＝CA＋7位数＋KD
        // APN＝CA＋7位数
        if (/^CA(\d{5})/i.test(apn)) {
            const kc = this._getKC(pn);
            const _pn = pn.replace('CA', '').replace(kc, '');
            const pt = this._getOfficialLinkCAPt(type);

            return (template[pt] && template[pt].replace(KEY_SYMBOL_REG, _pn)) as string;
        }
    }

    private _getOfficialLinkAU(apn: string) {
        // APN＝AU＋10位数
        if (/^AU(\d{10})$/i.test(apn)) {
            const key = apn.replace('AU', '');
            return AU_OFFICIAL_LINK_TEMPLATE.replace(KEY_SYMBOL_REG, key);
        }
    }

    private _getEspacenetLink(docdb: string, familyId: string, type: string[], template: ApiGetPatentTemplateResponse) {
        if (!docdb || !familyId || getIsDesignPatent(type)) return null;
        const kc = this._getKC(docdb);

        if (ESPACENET_SUFFIX.indexOf(kc) > -1) return null;

        const link = template.EPONEW
            .replace(FAMILY_ID_SYMBOL_REG, familyId)
            .replace(PN_SYMBOL_REG, docdb);

        return {
            name: 'Espacenet',
            link: link,
        };
    }

    private _getOfficialLink(pn: string, apn: string, pbd: string, type: string[], template: ApiGetPatentTemplateResponse) {
        const _pn = pn.toUpperCase();
        const _apn = apn.toUpperCase();
        const cc = _pn.substring(0, 2);
        const name: string | undefined = OFFICIAL_NAME_MAP[cc];
        let link: string | undefined;

        switch (cc) {
            case 'DE':
                link = this._getOfficialLinkDE(_pn, template);
                break;
            case 'EU':
                link = this._getOfficialLinkEU(_pn, template);
                break;
            case 'NO':
                link = this._getOfficialLinkNO(apn, template);
                break;
            case 'WO':
                link = this._getOfficialLinkWO(_pn, template);
                break;
            case 'CN':
                link = this._getOfficialLinkCN(_pn, pbd, template);
                break;
            case 'CA':
                link = this._getOfficialLinkCA(_pn, _apn, type, template);
                break;
            case 'AU':
                link = this._getOfficialLinkAU(apn);
                break;
            default:
                break;
        }

        if (!name || !link) return null;

        return {
            name,
            link: link,
        };
    }

    public async getPatentLink(input: GetPatentLinkInput): Promise<PatentLinkExtend> {
        try {
            const template = await this._getPatentTemplateApi.getPatentTemplate();

            const espacenet = this._getEspacenetLink(input.docdb, input.familyId, input.type, template);

            const official = this._getOfficialLink(input.pn, input.apn, input.pbd, input.type, template);

            const fullLinks = _.compact([espacenet, official]);

            const linksFilteredFormatted = fullLinks.map(item => {
                return {
                    name: '',
                    className: item.name,
                    link: item.link,
                };
            });

            return { linksFiltered: linksFilteredFormatted };
        } catch (error) {
            console.error(error);

            return { linksFiltered: [] };
        }
    }
}
